import { useCallback, useMemo } from 'react';
import { useAuth } from './auth-context';
import type { Permission, OrgRole } from './types';

/**
 * Hook for checking the current user's permissions in the active organization.
 *
 * Usage:
 *   const { can, canAny, canAll, role, permissions } = usePermissions();
 *   if (can('billing.manage')) { ... }
 *   if (canAny('inboxes.create', 'inboxes.update')) { ... }
 */
export function usePermissions() {
  const { currentOrganization } = useAuth();

  const permissions = useMemo<Set<Permission>>(() => {
    return new Set(currentOrganization?.permissions ?? []);
  }, [currentOrganization?.permissions]);

  const role = currentOrganization?.role as OrgRole | undefined;

  /** Check if the user has a specific permission */
  const can = useCallback(
    (permission: Permission): boolean => permissions.has(permission),
    [permissions],
  );

  /** Check if the user has ANY of the given permissions */
  const canAny = useCallback(
    (...required: Permission[]): boolean => required.some((p) => permissions.has(p)),
    [permissions],
  );

  /** Check if the user has ALL of the given permissions */
  const canAll = useCallback(
    (...required: Permission[]): boolean => required.every((p) => permissions.has(p)),
    [permissions],
  );

  /** Check if the user is the owner */
  const isOwner = role === 'owner';

  return {
    can,
    canAny,
    canAll,
    role,
    isOwner,
    permissions,
  };
}

// Role display info for the UI
export const ROLE_OPTIONS: Array<{ value: OrgRole; label: string; description: string }> = [
  { value: 'owner', label: 'Owner', description: 'Full access. Cannot be modified.' },
  { value: 'admin', label: 'Admin', description: 'Full management except billing & org deletion.' },
  { value: 'member', label: 'Member', description: 'View feedback, write notes, use search.' },
  { value: 'billing', label: 'Billing', description: 'Billing & subscription management only.' },
  { value: 'viewer', label: 'Viewer', description: 'Read-only access. No create/edit/delete.' },
];

/** Roles that can be assigned via the role dropdown (excludes owner) */
export const ASSIGNABLE_ROLE_OPTIONS = ROLE_OPTIONS.filter((r) => r.value !== 'owner');
